import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, FileText, Train, Plane, Ship } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple Markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return '';
  
  // Split into lines for processing
  const lines = text.split('\n');
  const elements = [];
  
  lines.forEach((line, lineIndex) => {
    // Process bold text (**text**)
    const parts = [];
    let remaining = line;
    let partIndex = 0;
    
    while (remaining.includes('**')) {
      const start = remaining.indexOf('**');
      if (start > 0) {
        parts.push(<span key={`${lineIndex}-${partIndex++}`}>{remaining.slice(0, start)}</span>);
      }
      remaining = remaining.slice(start + 2);
      const end = remaining.indexOf('**');
      if (end === -1) {
        parts.push(<span key={`${lineIndex}-${partIndex++}`}>**{remaining}</span>);
        remaining = '';
        break;
      }
      parts.push(<strong key={`${lineIndex}-${partIndex++}`} className="font-semibold">{remaining.slice(0, end)}</strong>);
      remaining = remaining.slice(end + 2);
    }
    
    if (remaining) {
      parts.push(<span key={`${lineIndex}-${partIndex++}`}>{remaining}</span>);
    }
    
    // Add line with proper spacing
    if (parts.length > 0) {
      elements.push(
        <span key={`line-${lineIndex}`}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    } else if (line === '') {
      elements.push(<br key={`br-${lineIndex}`} />);
    }
  });
  
  return elements;
};

export const ChatInterface = ({ user, chatId, chatData, onChatUpdate }) => {
  const navigate = useNavigate();
  
  const getInitialMessage = () => ({
    id: '1',
    role: 'assistant',
    content: user && user.role === 'guest'
      ? 'Guten Tag, ich bin Samira, Ihre digitale Assistentin bei GB Sylt. Wie kann ich Ihnen behilflich sein?'
      : `Guten Tag ${user?.name || ''}, ich bin Samira. Wie kann ich Ihnen heute helfen?`,
    timestamp: new Date().toISOString(),
    sources: []
  });

  const [messages, setMessages] = useState([getInitialMessage()]);
  const [currentChatId, setCurrentChatId] = useState(chatId || null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const isGuest = !user || user.role === 'guest';

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Load chat data when chatData changes (from sidebar selection)
  useEffect(() => {
    if (chatData && chatData.messages) {
      setMessages(chatData.messages);
      setCurrentChatId(chatData.id);
    }
  }, [chatData]);

  // Reset chat when chatId is null (new chat)
  useEffect(() => {
    if (chatId === null && !chatData) {
      setMessages([getInitialMessage()]);
      setCurrentChatId(null);
    }
  }, [chatId, chatData, user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const saveChatToServer = useCallback(async (msgs, title) => {
    if (isGuest) return null;
    
    const token = getToken();
    if (!token) return null;

    try {
      const response = await axios.post(`${API}/chat-history`, {
        chat_id: currentChatId,
        title: title,
        messages: msgs.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          sources: m.sources || []
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload sidebar chat list
      if (window.reloadChatHistory) {
        window.reloadChatHistory();
      }
      
      return response.data.chat_id;
    } catch (error) {
      console.error('Failed to save chat:', error);
      return null;
    }
  }, [currentChatId, isGuest]);

  const quickActions = user && user.role === 'guest'
    ? [
        { label: 'Anreise & Parken', query: 'Wie funktioniert die Anreise und wo kann ich parken?' },
        { label: 'WLAN-Zugang', query: 'Wie lautet das WLAN-Passwort?' },
        { label: 'Check-out', query: 'Wann muss ich auschecken und was muss ich beachten?' },
        { label: 'Müllentsorgung', query: 'Wo und wie entsorge ich den Müll?' }
      ]
    : [
        { label: 'Reinigungsprotokoll', query: 'Zeige mir das Reinigungsprotokoll' },
        { label: 'Schlüsselverwaltung', query: 'Wie funktioniert die Schlüsselverwaltung?' },
        { label: 'Gästebetreuung', query: 'Was sind die wichtigsten Punkte bei der Gästebetreuung?' },
        { label: 'Notfallkontakte', query: 'Zeige mir die wichtigsten Notfallkontakte' }
      ];

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const currentInput = input;
    setInput('');
    setIsLoading(true);

    try {
      const endpoint = 'chat/memo';
      
      const response = await axios.post(`${API}/${endpoint}`, null, {
        params: { question: currentInput, user_role: user?.role || 'guest' }
      });

      const botMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.content,
        timestamp: new Date().toISOString(),
        sources: response.data.sources || [],
        topic: response.data.topic,
        confidence: response.data.confidence
      };
      
      const updatedMessages = [...newMessages, botMessage];
      setMessages(updatedMessages);

      // Save chat (use first user message as title)
      const title = currentInput.slice(0, 50) + (currentInput.length > 50 ? '...' : '');
      const savedChatId = await saveChatToServer(updatedMessages, title);
      if (savedChatId && !currentChatId) {
        setCurrentChatId(savedChatId);
      }

    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        sources: []
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (query) => {
    setInput(query);
    textareaRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Chat messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-semibold">S</span>
                </div>
              )}
              
              <div className={`flex flex-col gap-1 max-w-[75%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`p-3 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-sm leading-relaxed">{renderMarkdown(message.content)}</div>
                </div>
                
                {message.sources && message.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {message.sources.map((source, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        {source}
                      </span>
                    ))}
                  </div>
                )}
                
                <span className="text-xs text-gray-400 px-1">
                  {formatTime(message.timestamp)}
                </span>
              </div>
              
              {message.role === 'user' && (
                <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-700 dark:text-gray-200 text-sm font-semibold">
                    {user?.name?.[0]?.toUpperCase() || 'G'}
                  </span>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-semibold">S</span>
              </div>
              <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="flex gap-1">
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions - only show for new chats */}
      {messages.length === 1 && messages[0].role === 'assistant' && (
        <div className="px-4 pb-3">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Häufige Fragen:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.query)}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
            
            {/* Anreise Buttons */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 mt-4">Anreise nach / von Sylt:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/anreise/autozug')}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#0E4C92] text-white hover:bg-[#0a3d78] transition-colors"
                data-testid="btn-autozug"
              >
                <Train className="w-4 h-4" />
                Autozug nach / von Sylt
              </button>
              <button
                onClick={() => navigate('/anreise/flug')}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#0E4C92] text-white hover:bg-[#0a3d78] transition-colors"
                data-testid="btn-flug"
              >
                <Plane className="w-4 h-4" />
                Flüge nach / von Sylt
              </button>
              <button
                onClick={() => navigate('/anreise/faehre')}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#0E4C92] text-white hover:bg-[#0a3d78] transition-colors"
                data-testid="btn-faehre"
              >
                <Ship className="w-4 h-4" />
                Fähre List ↔ Rømø
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Stellen Sie Ihre Frage..."
              className="flex-1 min-h-[50px] max-h-[150px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="h-[50px] px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Enter zum Senden, Shift+Enter für neue Zeile
          </p>
        </div>
      </div>
    </div>
  );
};
